import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlay, FiPlus, FiClock, FiEye, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const HeroSection = () => {
  const navigate = useNavigate();
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [autoPlayInterval, setAutoPlayInterval] = useState(null);

  const formatDuration = (seconds) => {
    if (isNaN(seconds) || seconds < 0) {
      return '00:00';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    
    const hDisplay = h > 0 ? String(h).padStart(2, '0') + ':' : '';
    const mDisplay = String(m).padStart(2, '0') + ':';
    const sDisplay = String(s).padStart(2, '0');
    
    return hDisplay + mDisplay + sDisplay;
  };

  useEffect(() => {
    const fetchFeaturedContent = async () => {
      try {
        const response = await fetch('https://christian-videos-api.onrender.com/featured');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();
        if (result.error || !result.data || result.data.length === 0) {
          throw new Error('Invalid data structure from API');
        }
        
        // Get up to 5 featured videos
        const videos = result.data.slice(0, 5).map(featured => ({
          id: featured.id,
          title: featured.video_title,
          description: featured.video_title,
          poster: `https:${featured.yt_thumb}`,
          playTime: featured.yt_length,
          views: featured.site_views,
          year: new Date().getFullYear(),
          tags: ['Featured'],
        }));
        
        setFeaturedVideos(videos);
      } catch (error) {
        console.error("Failed to fetch featured content:", error);
        // Fallback to mock data
        setFeaturedVideos([
          {
            id: '1',
            title: 'The Chosen',
            year: '2019',
            description: 'The first-ever multi-season series about the life of Christ.',
            tags: ['Drama', 'History', 'Faith'],
            poster: 'https://image.tmdb.org/t/p/original/sA217gwpbA4n3o02s2E1iWbNl3d.jpg',
            playTime: 3025,
            views: 1500000,
          },
          {
            id: '2',
            title: 'Faith in Action',
            year: '2020',
            description: 'Inspiring stories of faith and transformation.',
            tags: ['Documentary', 'Faith'],
            poster: 'https://via.placeholder.com/1920x1080?text=Faith+in+Action',
            playTime: 2400,
            views: 1200000,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedContent();
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (featuredVideos.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % featuredVideos.length);
      }, 5000); // Change slide every 5 seconds
      setAutoPlayInterval(interval);
      
      return () => clearInterval(interval);
    }
  }, [featuredVideos.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredVideos.length) % featuredVideos.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredVideos.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <div className="relative h-96 sm:h-screen w-full flex items-center justify-center bg-brand-dark">
        <div className="text-white text-xl">Loading Featured Content...</div>
      </div>
    );
  }

  if (!featuredVideos || featuredVideos.length === 0) {
    return (
      <div className="relative h-96 lg:h-[70vh] w-full flex items-center justify-center bg-brand-dark">
        <div className="text-white text-xl">Could not load featured content.</div>
      </div>
    );
  }

  const currentVideo = featuredVideos[currentIndex];

  return (
    <div className="relative h-96 lg:h-[70vh] w-full group">
      {/* Slider Container */}
      <div className="relative h-full w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={currentVideo.poster}
            alt={currentVideo.title}
            className="w-full h-full object-cover transition-all duration-700"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/50 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end h-full p-4 sm:p-8 md:p-12 lg:p-20 text-white">
          <div className="max-w-2xl">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 leading-tight">{currentVideo.title}</h1>
            
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm md:text-base text-brand-light-gray mb-2 sm:mb-4">
              <div className="flex items-center gap-2">
                <FiClock size={16} />
                <span>{formatDuration(currentVideo.playTime)}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiEye size={16} />
                <span className="hidden sm:inline">{currentVideo.views?.toLocaleString()} Views</span>
                <span className="sm:hidden">{(currentVideo.views / 1000000).toFixed(1)}M Views</span>
              </div>
              <span>{currentVideo.year}</span>
            </div>

            <p className="text-xs sm:text-base md:text-lg mb-4 sm:mb-6 max-w-xl line-clamp-2 sm:line-clamp-3">{currentVideo.description}</p>

            <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-base mb-6 sm:mb-8">
              {currentVideo.tags.map((tag, index) => (
                <React.Fragment key={tag}>
                  <span>{tag}</span>
                  {index < currentVideo.tags.length - 1 && (
                    <span className="text-gray-500">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <button 
                onClick={() => navigate(`/watch/${currentVideo.id}`)}
                className="flex items-center gap-2 px-4 sm:px-8 py-2 sm:py-3 rounded-md bg-gradient-accent text-white font-bold text-sm sm:text-lg hover:scale-105 transition-transform"
              >
                <FiPlay size={20} className="sm:w-6 sm:h-6" />
                <span>Watch Now</span>
              </button>
              <button className="flex items-center justify-center w-10 sm:w-14 h-10 sm:h-14 rounded-md bg-white/20 hover:bg-white/30 transition-colors">
                <FiPlus size={24} className="sm:w-8 sm:h-8" />
              </button>
            </div>
          </div>
        </div>

        {/* Left Arrow */}
        {featuredVideos.length > 1 && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 hover:bg-white/40 transition-all opacity-0 group-hover:opacity-100"
          >
            <FiChevronLeft size={24} />
          </button>
        )}

        {/* Right Arrow */}
        {featuredVideos.length > 1 && (
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 hover:bg-white/40 transition-all opacity-0 group-hover:opacity-100"
          >
            <FiChevronRight size={24} />
          </button>
        )}
      </div>

      {/* Dots Indicator */}
      {featuredVideos.length > 1 && (
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {featuredVideos.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentIndex ? 'w-8 bg-gradient-accent' : 'w-2 bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroSection;
